import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Menu, Mail, CreditCard, Settings, LogOut, Download, Clock } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { ContactDialog } from "./ContactDialog";
import { openBillingPortal } from "./ManageBillingButton";

// Shared top-right hamburger menu for both apps: Contact Us, My Account,
// Preferences (BetMaker only), Logout. `theme` matches each app's palette.
export const AppMenu = ({ user, onLogout, showPreferences = false, onOpenPreferences, theme = "dark" }) => {
  const navigate = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);

  const dark = theme === "dark";
  const triggerClass = dark
    ? "inline-flex items-center justify-center h-9 w-9 rounded-full border border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-emerald-400 transition"
    : "inline-flex items-center justify-center h-9 w-9 rounded-full border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-emerald-600 transition";
  const contentClass = dark
    ? "bg-slate-900 border-slate-700 text-slate-100"
    : "bg-white border-slate-200 text-slate-900";
  const itemClass = `cursor-pointer ${dark ? "focus:bg-slate-800 focus:text-emerald-400" : "focus:bg-slate-100 focus:text-emerald-600"}`;

  const handleAccount = async () => {
    try { await openBillingPortal(navigate); }
    catch (e) { toast.error(e.message || "Could not open billing portal"); }
  };

  // Day-pass expiry (display only; logic unchanged). Access is gated at the UTC
  // calendar-day level, so the pass actually expires at 00:00 UTC of the day AFTER
  // subscription_ends_at's UTC date. Show that instant in the user's local timezone.
  const dayPassExpiry = (() => {
    if (user?.subscription_plan !== "day_pass" || user?.subscription_status !== "active" || !user?.subscription_ends_at) return null;
    const ends = new Date(user.subscription_ends_at);
    if (isNaN(ends.getTime())) return null;
    const expiry = new Date(Date.UTC(ends.getUTCFullYear(), ends.getUTCMonth(), ends.getUTCDate() + 1, 0, 0, 0));
    if (expiry.getTime() <= Date.now()) return null;
    return expiry.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  })();
  const badgeClass = dark
    ? "border-orange-400/40 bg-orange-500/15 text-orange-300"
    : "border-orange-300 bg-orange-100 text-orange-700";

  return (
    <div className="inline-flex items-center gap-2">
      {dayPassExpiry && (
        <span data-testid="daypass-expiry-badge"
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${badgeClass}`}
          title={`Your Day Pass expires ${dayPassExpiry} (your local time)`}>
          <Clock className="w-3.5 h-3.5" /> Day Pass expires {dayPassExpiry}
        </span>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={triggerClass} data-testid="app-menu-trigger" title="Menu" aria-label="Menu">
            <Menu className="w-5 h-5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={`w-52 ${contentClass}`} data-testid="app-menu-content">
          {user?.email && (
            <div className="px-2 py-1.5 text-xs text-slate-400 truncate" data-testid="app-menu-email">{user.email}</div>
          )}
          <DropdownMenuItem className={itemClass} data-testid="app-menu-contact"
            onSelect={() => setTimeout(() => setContactOpen(true), 0)}>
            <Mail className="w-4 h-4 mr-2" /> Contact Us
          </DropdownMenuItem>
          <DropdownMenuItem className={itemClass} data-testid="app-menu-downloads"
            onSelect={() => setTimeout(() => navigate("/my-downloads"), 0)}>
            <Download className="w-4 h-4 mr-2" /> My Downloads
          </DropdownMenuItem>
          <DropdownMenuItem className={itemClass} data-testid="app-menu-account"
            onSelect={() => setTimeout(handleAccount, 0)}>
            <CreditCard className="w-4 h-4 mr-2" /> My Account
          </DropdownMenuItem>
          {showPreferences && (
            <DropdownMenuItem className={itemClass} data-testid="app-menu-preferences"
              onSelect={() => setTimeout(() => onOpenPreferences?.(), 0)}>
              <Settings className="w-4 h-4 mr-2" /> Preferences
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator className={dark ? "bg-slate-700" : "bg-slate-200"} />
          <DropdownMenuItem className={`cursor-pointer text-red-500 focus:text-red-500 ${dark ? "focus:bg-slate-800" : "focus:bg-slate-100"}`}
            data-testid="app-menu-logout" onSelect={() => setTimeout(() => onLogout?.(), 0)}>
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} hideTrigger defaultEmail={user?.email || ""} />
    </div>
  );
};

export default AppMenu;
